import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { useState, useMemo, useEffect } from 'react';
import TitleField from '../TitleField';
import { Card, Typography, Chip, Table, TableBody, TableRow, TableCell, Tabs, Tab, Divider, Box } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    width: '100%',
  },
  section: {
    marginTop: theme.spacing(2),
  },
  label: {
    fontWeight: 500,
    color: '#666',
    fontSize: 13,
  },
  successChip: {
    backgroundColor: '#e6f4ea',
    color: '#1e7e34',
    fontWeight: 600,
  },
  failChip: {
    backgroundColor: '#fdecea',
    color: '#c62828',
    fontWeight: 600,
  },
  imageContainer: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  image: {
    width: '100%',
    maxHeight: 300,
    objectFit: 'contain',
    borderRadius: 8,
    background: '#f5f5f5',
  },
}));

const formatFieldValue = (fieldType, value) => {
  if (!value) return '-';

  switch (fieldType) {
    case 'Date of birth':
    case 'Date of issue': {
      const parsed = new Date(value);
      return isNaN(parsed.getTime())
        ? value
        : parsed.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
    }

    case 'Sex':
      return value === 'M' ? 'Male' : value === 'F' ? 'Female' : value;

    case 'Address':
      return value.split('\n').map((line, i) => (
        <span key={i}>
          {line}
          <br />
        </span>
      ));

    default:
      return value;
  }
};

const labelMap = {
  'Surname and given names': 'Full Name',
  'Document number': 'NRIC',
};

const importantFields = ['Document number', 'Surname and given names', 'Date of birth', 'Sex', 'Race ethnicity', 'Issuing state name'];

const RegtankDocumentTable = ({ documents = {}, docField = {}, docImage = {}, className }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);

  const docInfo = documents?.documentInfo || {};
  const idvV1Info = documents?.idvV1Info || {};
  const extensions = idvV1Info?.documentExtensions || [];
  const fields = idvV1Info?.ocrV2Info?.documentFields || [];
  const ocrStatus = documents?.ocrResults?.status;

  const hasDocField = Array.isArray(docField) && docField.length > 0;

  const mergedFields = useMemo(() => {
    const df = Array.isArray(docField) ? docField : [];
    const flds = Array.isArray(fields) ? fields : [];

    return df.map((dfItem) => {
      const match = flds.find((f) => f.fieldType === dfItem.fieldType);

      return {
        fieldType: dfItem.fieldType,
        label: labelMap[dfItem.fieldType] || dfItem.fieldType,
        value: match?.visualZone || match?.barcode || match?.mrz || '-',
        valid: dfItem.valid,
      };
    });
  }, [docField, fields]);

  const fallbackFields = useMemo(() => {
    return (Array.isArray(fields) ? fields : []).map((f) => ({
      fieldType: f.fieldType,
      label: labelMap[f.fieldType] || f.fieldType,
      value: f.visualZone || f.barcode || f.mrz || '-',
      valid: null,
    }));
  }, [fields]);

  const displayFields = hasDocField ? mergedFields : fallbackFields;

  const errorFields = displayFields.filter((f) => f.valid === 'ERROR');
  const mainFields = displayFields.filter((f) => importantFields.includes(f.fieldType));
  const otherFields = displayFields.filter((f) => !importantFields.includes(f.fieldType));

  const renderChip = (status) => {
    if (!status) return null;
    if (status === 'OK') return <Chip label="OK" size="small" className={classes.successChip} />;
    if (status === 'ERROR') return <Chip label="ERROR" size="small" className={classes.failChip} />;
    return null;
  };

  // Tabs
  const tabs = [
    { label: 'Front', value: docInfo.frontDocumentUrl },
    { label: 'Back', value: docInfo.backDocumentUrl },
    ...extensions
      .filter((ext) => ['Portrait', 'Barcode', 'Fingerprint', 'Ghost portrait'].includes(ext.key))
      .map((ext) => ({
        label: ext.key,
        value: ext.value,
      })),
  ].filter((t) => t.value);

  // 🔥 CURRENT TAB LOGIC
  const currentTab = tabs[tab]?.label;

  const currentImageData = currentTab === 'Front' ? docImage?.front : currentTab === 'Back' ? docImage?.back : null;

  useEffect(() => {
    console.log(docImage);
  }, [docImage]);

  return (
    <Card className={clsx(classes.card, className)}>
      <TitleField>ID Photo</TitleField>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable">
        {tabs.map((t, i) => (
          <Tab key={i} label={t.label} />
        ))}
      </Tabs>

      <Box className={classes.imageContainer}>{tabs[tab]?.value && <img src={tabs[tab].value} alt={tabs[tab].label} className={classes.image} />}</Box>

      {/* 🔥 IMAGE QUALITY */}
      {currentImageData && (
        <Box className={classes.section}>
          <Typography style={{ fontWeight: 600, marginBottom: 8 }}>📷 Image Quality ({currentTab})</Typography>

          {Object.entries(currentImageData).map(([key, value]) => {
            if (!value || value === 'WAS_NOT_DONE') return null;

            const isError = value === 'ERROR';

            return (
              <Box key={key} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #eee" py={1}>
                <Typography style={{ color: '#666' }}>{key.replace(/([A-Z])/g, ' $1')}</Typography>

                <Chip label={value} size="small" className={isError ? classes.failChip : classes.successChip} />
              </Box>
            );
          })}
        </Box>
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* 🔴 Issues */}
      {errorFields.length > 0 && (
        <Box className={classes.section}>
          <Typography style={{ fontWeight: 600, marginBottom: 8 }}>⚠️ Issues Found</Typography>

          <Table>
            <TableBody>
              {errorFields.map((f, i) => (
                <TableRow key={i} style={{ background: '#fff5f5' }}>
                  <TableCell className={classes.label}>{f.label}</TableCell>
                  <TableCell>{formatFieldValue(f.fieldType, f.value)}</TableCell>
                  <TableCell align="right">{renderChip(f.valid)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {(mainFields.length > 0 || otherFields.length > 0) && (
        <Box className={classes.section}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <TitleField>Verification Summary</TitleField>

            {ocrStatus && (
              <Box
                px={1.5}
                py={0.5}
                style={{
                  background: ocrStatus === 'SUCCESS' ? '#e6f7ee' : '#ffeaea',
                  color: ocrStatus === 'SUCCESS' ? '#2cc990' : '#ff4d4f',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 12,
                }}>
                {ocrStatus}
              </Box>
            )}
          </Box>

          <br></br>

          {/* 🟢 Identity */}
          {mainFields.length > 0 && (
            <>
              <Typography style={{ fontWeight: 600, marginBottom: 8 }}>👤 Identity</Typography>

              {mainFields.map((f, i) => (
                <Box key={`main-${i}`} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #eee" py={1}>
                  <Typography style={{ color: '#666' }}>{f.label}</Typography>

                  <Typography component="div" style={{ fontWeight: 500 }}>
                    {formatFieldValue(f.fieldType, f.value)}
                  </Typography>
                </Box>
              ))}
            </>
          )}

          {/* 🟡 Additional */}
          {otherFields.length > 0 && (
            <>
              <Typography
                style={{
                  fontWeight: 600,
                  marginTop: 16,
                  marginBottom: 8,
                }}>
                📄 Additional Details
              </Typography>

              {otherFields.map((f, i) => (
                <Box key={`other-${i}`} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #eee" py={1}>
                  <Typography style={{ color: '#666' }}>{f.label}</Typography>

                  <Typography component="div" style={{ fontWeight: 500 }}>
                    {formatFieldValue(f.fieldType, f.value)}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Card>
  );
};

export default RegtankDocumentTable;
